"use client";

import { useState, useRef } from "react";
import { TypeAnimation } from "react-type-animation";
import { cn } from "@/lib/utils";

const PAUSE_MS = 1000;

const DEFAULT_SUGGESTIONS = [
  "Track Zillow listings in my area and alert me when prices drop",
  "Monitor Hacker News and send me a daily digest of AI papers",
  "Watch my competitors' pricing pages and notify me of changes",
  "Scrape job boards daily and email me matching senior roles",
  "Track flight prices to Tokyo and book when they drop below $500",
];

interface PromptTextareaProps {
  placeholder?: string;
  suggestions?: string[];
  onSubmit: (message: string) => void;
  className?: string;
}

export function PromptTextarea({
  placeholder = "Build your agent for free. No login required.",
  suggestions = DEFAULT_SUGGESTIONS,
  onSubmit,
  className,
}: PromptTextareaProps) {
  const sequence = suggestions.flatMap((s) => [s, PAUSE_MS]);
  const [value, setValue] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const hasText = value.trim().length > 0;
  const showAnimation = !isFocused && !hasText;

  const handleSubmit = () => {
    const message = value.trim();
    if (message) {
      onSubmit(message);
    }
  };

  return (
    <div className={cn("relative w-full", className)}>
      {showAnimation && (
        <div
          className="pointer-events-none absolute inset-0 flex items-start px-5 py-4 pr-16 text-base text-white/30"
          aria-hidden
        >
          <TypeAnimation
            sequence={[...sequence]}
            repeat={Infinity}
            speed={60}
            deletionSpeed={80}
            wrapper="span"
            cursor={false}
          />
        </div>
      )}
      <textarea
        ref={textareaRef}
        placeholder={isFocused ? placeholder : ""}
        rows={2}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        className="w-full bg-white/[0.12] border border-white/20 rounded-lg px-5 py-4 pr-16 text-base text-white placeholder-white/30 outline-none focus:border-white/35 transition-colors resize-none"
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            handleSubmit();
          }
        }}
      />
      <button
        onClick={handleSubmit}
        disabled={!hasText}
        className={cn(
          "absolute right-2 bottom-3.5 flex h-9 w-9 items-center justify-center rounded-lg transition-all duration-200",
          hasText
            ? "bg-white cursor-pointer hover:bg-white/90"
            : "bg-white/15 cursor-default"
        )}
        aria-label="Submit"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M8 13V3M8 3L3 8M8 3L13 8"
            stroke={hasText ? "#0a0a0a" : "rgba(255,255,255,0.3)"}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </div>
  );
}
